if (!customElements.get('all-collection-products')) {
  customElements.define(
    'all-collection-products',
    class extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        this.style.display = 'block';
        const signal = this.controller.signal;
        this.gestureConsumed = false;
        this.nextLoadAllowedAt = 0;
        this.lastWheelTime = -Infinity;
        // Scroll events also fire for layout shifts and browser scroll anchoring.
        // Only real user input can request another batch; never visibility alone.
        window.addEventListener('wheel', (event) => {
          if (!event.isTrusted) return;
          if (event.timeStamp - this.lastWheelTime > 350) this.gestureConsumed = false;
          this.lastWheelTime = event.timeStamp;
          if (event.deltaY > 0) this.loadFromGesture();
        }, { passive: true, signal });
        window.addEventListener('touchstart', (event) => {
          this.touchStartY = event.touches[0]?.clientY;
          this.gestureConsumed = false;
        }, { passive: true, signal });
        window.addEventListener('touchmove', (event) => {
          if (event.isTrusted && this.touchStartY - event.touches[0]?.clientY > 20) this.loadFromGesture();
        }, { passive: true, signal });
        window.addEventListener('keydown', (event) => {
          if (!event.isTrusted || event.repeat || event.defaultPrevented || event.shiftKey) return;
          if (event.target.closest('input, textarea, select, button, a, [contenteditable]')) return;
          if (!['ArrowDown', 'PageDown', 'End', ' '].includes(event.key)) return;
          this.gestureConsumed = false;
          this.loadFromGesture();
        }, { signal });
        this.querySelector('a')?.addEventListener('click', (event) => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || this.failed) return;
          event.preventDefault();
          this.loadProducts(signal);
        }, { signal });
      }

      loadFromGesture() {
        if (this.loading || this.failed || this.gestureConsumed) return;
        const bounds = this.getBoundingClientRect();
        if (bounds.top >= window.innerHeight || bounds.bottom <= 0) return;
        this.gestureConsumed = true;
        this.loadProducts(this.controller.signal);
      }

      disconnectedCallback() {
        this.controller.abort();
      }

      async loadProducts(signal) {
        if (this.loading || signal.aborted || Date.now() < this.nextLoadAllowedAt) return;
        const sectionId = this.dataset.sectionId;
        const grid = document.getElementById(`Slider-${sectionId}`);
        const link = this.querySelector('a');
        if (!grid || !link) return;

        this.loading = true;
        this.gestureConsumed = true;
        this.setAttribute('aria-busy', 'true');
        try {
            const url = new URL(link.href);
            url.searchParams.set('section_id', sectionId);
            const response = await fetch(url, { signal });
            if (!response.ok) throw new Error(`Product request failed: ${response.status}`);

            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const nextGrid = html.getElementById(`Slider-${sectionId}`);
            if (!nextGrid || !nextGrid.children.length) throw new Error('Missing product grid');
            if (signal.aborted) return;

            // Newly inserted cards are outside the initial animation observer.
            nextGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
              element.classList.remove('scroll-trigger', 'animate--slide-in');
            });
            const nextLink = html.querySelector('all-collection-products a');
            const nextUrl = nextLink ? new URL(nextLink.getAttribute('href'), link.href) : null;
            if (nextUrl?.href === link.href) throw new Error('Repeated product page');
            grid.append(...nextGrid.children);
            if (!nextLink) {
              this.remove();
              return;
            }
            link.href = nextUrl.href;
        } catch (error) {
          // Keep the regular next-page link usable if automatic loading fails.
          if (error.name !== 'AbortError') {
            this.failed = true;
            console.error(error);
          }
        } finally {
          // A cooldown never schedules a request. A fresh input is required afterwards.
          this.nextLoadAllowedAt = Date.now() + 3000;
          this.gestureConsumed = true;
          this.loading = false;
          this.removeAttribute('aria-busy');
        }
      }
    }
  );
}
